<script setup>
import { reactive, ref, onMounted, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useEventsStore } from '@/stores/events';
import Modal from '@/Components/Partials/Modal.vue';
import FormField from '@/Components/Partials/FormField.vue';
import Select from '@/Components/Partials/Select.vue';

const portal = ref(window.portal);
const labels = ref(window.portal.labels);
const key = ref(window.portal.event_key);

const eventsStore = useEventsStore();
const authStore = useAuthStore();

const registrationData = reactive({
	event: null,
	main_activity: null,
	delegates: [],
	invoice: null,
	registrations: []
});

const loading = ref(true);
const registrationClosed = ref(false);

const form = reactive({
    registration_id: null,
    guest_of: null,
    attendee_form_id: null,
    activity_id: null,
    delegate_id: '',
    fields: {},
    processing: false
});

const resetForm = () => {
	form.registration_id = null;
	form.guest_of = null;
	form.delegate_id = '';
	form.processing = false;
}

const fetchRegistrationData = async() => {
	try {
		const response = await axios.get(`${window.portal.events_url}/${key.value}/register`);

		//Set registrationData (aka props)
		registrationData.event = response.data.event;
		registrationData.main_activity = response.data.main_activity;
		registrationData.delegates = response.data.delegates;
		registrationData.invoice = response.data.invoice;
		registrationData.registrations = response.data.registrations;

		//Set attendee form values
		form.attendee_form_id = registrationData.main_activity.form.id;
		form.activity_id = registrationData.main_activity.id;
	}
	catch (error) {
		if (error && error.status && error.status == 403) {
			registrationClosed.value = true;
		}
	} finally {
		loading.value = false;
	}
}

//Fetch every time and update store
fetchRegistrationData();

const showAttendeeModal = ref(false);
const guestRegistration = ref(false);

const guestsFull = computed(() => {
    return registrationData.delegates.filter((delegate) => delegate.is_attending && !delegate.has_guest).length == 0;
});

const filteredDelegates = computed(() => {
    //List of applicable delegates for guests
    if (guestRegistration.value == true) {
        return registrationData.delegates.filter((delegate) => (delegate.is_attending && !delegate.has_guest && delegate.event_registration != null) || (editingRegistration.value && editingRegistration.value.delegate_id == delegate.id));
    }

    //List of applicable delegates for new registrations
    return registrationData.delegates.filter((delegate) => !delegate.is_attending || (editingRegistration.value && editingRegistration.value.delegate_id == delegate.id));
});

const attendingDelegates = computed(() => {
    return registrationData.delegates.filter(delegate => 
        !filteredDelegates.value.includes(delegate)
    );
});

const delegateRegistrations = computed(() => {
    return registrationData.registrations.filter((registration) => registration.guest_of == null);
});

const openAttendeeModal = () => {
    if (!editingRegistration.value) {
        //Set dynamic form fields
        registrationData.main_activity.form.fields.forEach((field) => {
            form.fields[field.name] = '';
        });
    }
    
    showAttendeeModal.value = true;
}

watch(() => form.delegate_id, (delegate_id) => {
    //Try to set the name field, if there is a name field, and this isn't a guest registration
    if (delegate_id && !guestRegistration.value) {
        //Find the delegate
        let delegate = filteredDelegates.value.find(del => del.id == delegate_id);

        if (delegate) {
            //Find the form field and update if it's empty
            registrationData.main_activity.form.fields.forEach((field) => {
                if (field.name == 'name' && !form.fields[field.name].length) {
                    form.fields[field.name] = delegate.name;
                }
            });
        }
    }
});

const closeAttendeeModal = () => {
    showAttendeeModal.value = false;
    resetForm();
    editingRegistration.value = null;
    guestRegistration.value = false;
}

const submitAttendeeForm = () => {
    if (form.processing) return;

    //If guest, set the guest of property
    if (guestRegistration.value) {
        let delegate = filteredDelegates.value.find(del => del.id == form.delegate_id);
        form.guest_of = delegate.event_registration.id;
    }

    let url = '';

    //If we don't have an invoice yet, then submit the form to start a new invoice and save the event registration (and activity registration for delegates)
    if (!registrationData.invoice) {
        url = route('user.events.register.store', {event: registrationData.event.id});
    }
    //We might be updating an existing registration
    else if (form.registration_id) {
        url = route('user.registrations.update', {registration: form.registration_id});
    }
    //Otherwise we are adding a registration for an existing invoice
    else {
        url = route('user.events.register.attend', {event: registrationData.event.id, invoice: registrationData.invoice.id});
    }

    //Submit the form
    // form.post(url, {
    //     onSuccess: () => closeAttendeeModal()
    // });
}

const editingRegistration = ref(null);

const editAttendeeForm = (registration) => {
    //Set all the form values
    editingRegistration.value = registration;
    form.registration_id = registration.id;
    form.delegate_id = registration.delegate_id;

    registrationData.main_activity.form.fields.forEach((field) => {
        let matchingValue = registration.entry.values.find((value) => value.name === field.name);
        form.fields[field.name] = matchingValue ? matchingValue.value : '';
    });

    //Handle guest
    if (registration.guest_of) {
        guestRegistration.value = true;
        form.guest_of = registration.guest_of;
    }

    //Now show the modal
    openAttendeeModal();
}

const deletingRegistration = ref(null);
const deleteForm = reactive({
	processing: false
});

const openDeleteConfirmation = (registration) => {
    deletingRegistration.value = registration;
}

const closeDeleteConfirmation = () => {
    deletingRegistration.value = null;
}

const removeAttendee = () => {
    if (deleteForm.processing) return;
    
    // deleteForm.post(route('user.events.register.delete', {event: registrationData.event.id, invoice: registrationData.invoice.id, registration: deletingRegistration.value.id}), {
    //     onSuccess: () => closeDeleteConfirmation()
    // });
};

const attendeeModalHeading = computed(() => {
    let message = form.registration_id ? labels.value.edit : labels.value.add;
    message += guestRegistration.value == true ? ` ${labels.value.companion}` : ` ${labels.value.delegate}`;

    return message;
});

const attendeeModalDescription = computed(() => {
    return guestRegistration.value == true ? labels.value.delegates_may_bring_a_companion : labels.value.delegate_registration;
});

const attendeeModalButton = computed(() => {
    return form.registration_id ? labels.value.save_changes : labels.value.save;
});

const delegateSelectLabel = computed(() => {
    return guestRegistration.value == true ? labels.value.companion_of : labels.value.delegate;
});

const removeAttendeeLabel = computed(() => {
    return deletingRegistration.value.guest_of ? labels.value.companion : labels.value.delegate;
});

const openGuestModal = () => {
    guestRegistration.value = true;
    openAttendeeModal();
}

const isOverbooked = computed(() => {
    return registrationData.main_activity.spots_remaining !== null && registrationData.main_activity.spots_remaining < delegateRegistrations.length;
});
</script>

<template>
	<div class="wp-block-group is-layout-constrained alignfull" style="padding-left: var(--wp--preset--spacing--50); padding-right: var(--wp--preset--spacing--50); margin-top: var(--wp--preset--spacing--60); margin-bottom: var(--wp--preset--spacing--70);">
		<template v-if="registrationClosed">
			<p class="alignwide" v-html="labels.registrations_closed"></p>
			<div class="wp-block-buttons alignwide" style="margin-top: var(--wp--preset--spacing--40);">
				<div class="wp-block-button is-style-event-arrow-back">
					<a class="wp-block-button__link" v-html="labels.back" :href="eventsStore.getEventUrl(key)"></a>
				</div>
			</div>
		</template>

		<template v-else-if="registrationData.event">
			<div class="wp-block-group is-layout-flex alignwide registration-header" style="padding: var(--wp--preset--spacing--30) var(--wp--preset--spacing--40); border: solid 1px var(--wp--preset--color--admin-grey-200); border-radius: 1rem; gap: var(--wp--preset--spacing--10);">
				<h2 class="has-large-font-size" v-html="labels.attendee_information" style="margin-right: auto;"></h2>

				<span class="step-number active">1</span>
				<span class="step-number-divider"></span>
				<span class="step-number">2</span>
				<span class="step-number-divider"></span>
				<span class="step-number">3</span>
			</div>

			<p class="has-small-font-size alignwide" style="margin-top: var(--wp--preset--spacing--40);">{{registrationData.event.allows_guests ? labels.register_new_delegates_and_companions : labels.register_new_delegates }} {{ labels.activities_will_be_selected }}</p>

			<div class="wp-block-buttons is-layout-flex alignwide" style="gap: var(--wp--preset--spacing--20);">
				<div class="wp-block-button">
					<button
						class="wp-block-button__link has-background-color has-fc-black-background-color"
						@click.prevent="openAttendeeModal"
						:disabled="registrationData.main_activity.is_full || (registrationData.main_activity.spots_remaining !== null && delegateRegistrations.length >= registrationData.main_activity.spots_remaining)"
						v-html="labels.add_delegate"
					></button>
				</div>

				<div class="wp-block-button">
					<button
						class="wp-block-button__link has-background-color has-fc-black-background-color"
						v-if="registrationData.event.allows_guests"
						@click.prevent="openGuestModal"
						:disabled="guestsFull"
						v-html="labels.add_companion"
					></button>
				</div>
	        </div>

			<div class="wp-block-buttons is-layout-flex alignwide" style="border-top: solid 1px var(--wp--preset--color--admin-grey-200); padding-top: var(--wp--preset--spacing--50); gap: var(--wp--preset--spacing--20);">
				<div class="wp-block-button is-style-event-arrow-back">
					<a class="wp-block-button__link" v-html="labels.back"></a>
				</div>

				<div class="wp-block-button is-style-event-arrow-next">
					<a class="wp-block-button__link" v-html="labels.next"></a>
				</div>
			</div>

			<Modal :show="showAttendeeModal" @close="closeAttendeeModal">
                <h2 class="has-large-font-size">{{ attendeeModalHeading }}</h2>

                <p class="has-small-font-size" v-html="attendeeModalDescription"></p>

                <!-- Attendee Form -->
                <form @submit.prevent="submitAttendeeForm" v-if="registrationData.main_activity">
                    <div class="wp-block-group is-layout-grid columns-2">
                        <div>
                            <label for="delegate" v-html="`${delegateSelectLabel}*`"></label>
                            <Select
                                id="delegate"
                                v-model="form.delegate_id"
                                :disabled="form.registration_id"
                            >
                                <option value="" v-html="labels.select_delegate"></option>
                                <option v-for="delegate in filteredDelegates" :value="delegate.id" v-html="delegate.name"></option>
                                <optgroup v-if="attendingDelegates.length" :label="guestRegistration ? labels.ineligible : labels.registered">
                                        <option v-for="delegate in attendingDelegates" value="" v-html="delegate.name" disabled></option>
                                    </optgroup>
                            </Select>
                        </div>

                        <div v-for="field in registrationData.main_activity.form.fields">
                            <label :for="field.name" v-html="`${field.label}${field.required ? '*' : ''}`"></label>
                            <FormField
                            	v-if="form.fields.hasOwnProperty(field.name)"
                                v-model="form.fields[field.name]"
                                :id="field.name"
                                :type="field.type"
                                :required="field.required"
                                :choices="field.choices"
                                :placeholder="field.placeholder"
                            />
                        </div>
                    </div>

                    <div class="wp-block-buttons is-layout-flex" style="gap: var(--wp--preset--spacing--10); justify-content: center; margin-top: var(--wp--preset--spacing--40);">
                    	<div class="wp-block-button">
	                        <a class="wp-block-button__link has-background-color has-fc-black-background-color has-small-font-size" style="padding: var(--wp--preset--spacing--10) var(--wp--preset--spacing--20);" @click.prevent="submitAttendeeForm">{{ attendeeModalButton }}</a>
	                    </div>

	                    <div class="wp-block-button">
	                    	<a class="wp-block-button__link has-background-color has-admin-grey-200-background-color has-text-color has-fc-black-color has-small-font-size" style="padding: var(--wp--preset--spacing--10) var(--wp--preset--spacing--20);" @click="closeAttendeeModal" v-html="labels.cancel"></a>
	                    </div>
                    </div>
                </form>
			</Modal>
		</template>
	</div>
</template>
